import {
  MilestoneCamera,
  PatrolClipSeconds,
  PatrolInterval,
  PatrolSchedule,
  PatrolVideoSource,
  PipelineSource,
  PipelineSourceWrite,
  PipelineType
} from '@/types/vlm'
import { getBrowserTimezone } from '@/utils/date'
import { generateUUID } from '@/utils/uuid'
import {
  HTTP_PUSH_DEFAULT_PORT,
  MILESTONE_DEFAULT_POSTROLL_SECONDS,
  MILESTONE_DEFAULT_PREROLL_SECONDS,
  MILESTONE_DEFAULT_TRIGGER_STATE,
  PATROL_DEFAULT_CLIP_SECONDS,
  PATROL_DEFAULT_INTERVAL,
  PATROL_DEFAULT_ROUND_TIMEOUT_MINUTES,
  PATROL_DEFAULT_START_TIME,
  PATROL_DEFAULT_TIMES
} from '../vlmPipelineConfig'
import {
  detectPatrolInterval,
  expandPatrolInterval,
  roundTimeoutToMinutes,
  roundTimeoutToSeconds
} from './patrol/patrolSchedule'

// A patrol time ("HH:mm") with a key, so rows can be removed safely.
export type PatrolTimeFormValue = { uuid: string; value: string }

export const toPatrolTimeFormValues = (times: string[]) =>
  times.map((value) => ({ uuid: generateUUID(), value }))

export const getPatrolTimeValues = (times: PatrolTimeFormValue[]) =>
  times.map((time) => time.value)

export type PipelineSourceFormValues = {
  watchPath: string
  deleteOriginal: boolean
  endpointPath: string
  port: number
  applicationId: string
  uploadDescriptions: boolean
  milestoneServerId: string
  triggerStates: string[]
  prerollSeconds: number
  postrollSeconds: number
  milestoneCameras: MilestoneCamera[]
  patrolVideoSource: PatrolVideoSource
  // Virtual patrol. A separate server field from milestoneServerId so that
  // switching the type never carries one source's server into the other.
  patrolServerId: string
  patrolCameras: MilestoneCamera[]
  // 'every' is the interval mode of the form. It is sent as fixed times
  // expanded from patrolStartTime (see expandPatrolInterval).
  patrolScheduleMode: PatrolSchedule['mode']
  // The other mode's values are kept while switching modes.
  patrolTimes: PatrolTimeFormValue[]
  // Shared by fixed times and intervals.
  patrolTimezone: string
  patrolInterval: PatrolInterval
  patrolStartTime: string
  // UI-only: a schedule saved with the start-less interval mode keeps that
  // mode until its schedule is edited, so an unrelated edit can't move it.
  patrolLegacyEvery: boolean
  patrolClipSeconds: PatrolClipSeconds
  patrolRoundTimeoutMinutes: number
  // UI-only: stop suggesting a round timeout once the user has edited it.
  patrolRoundTimeoutTouched: boolean
  patrolMilestoneActionsRemoved: boolean
}

export const getSourceInitialValues = (
  source?: PipelineSource
): PipelineSourceFormValues => ({
  ...getPatrolInitialValues(source),
  watchPath: source?.type === 'directory' ? source.watch_path : '',
  deleteOriginal: source?.type === 'directory' ? source.delete_original : false,
  endpointPath: source?.type === 'http_push' ? source.endpoint_path : '',
  port: source?.type === 'http_push' ? source.port : HTTP_PUSH_DEFAULT_PORT,
  applicationId:
    source?.type === 'visionplatform' ? (source.applications[0]?.id ?? '') : '',
  uploadDescriptions:
    source?.type === 'visionplatform' ? source.upload_descriptions : true,
  milestoneServerId:
    source?.type === 'milestone' ? (source.milestone_server?.id ?? '') : '',
  triggerStates:
    source?.type === 'milestone'
      ? source.process_when_states
      : [MILESTONE_DEFAULT_TRIGGER_STATE],
  prerollSeconds:
    source?.type === 'milestone'
      ? source.preroll_seconds
      : MILESTONE_DEFAULT_PREROLL_SECONDS,
  postrollSeconds:
    source?.type === 'milestone'
      ? source.postroll_seconds
      : MILESTONE_DEFAULT_POSTROLL_SECONDS,
  milestoneCameras: source?.type === 'milestone' ? source.cameras : []
})

const getPatrolInitialValues = (source?: PipelineSource) => {
  const patrol = source?.type === 'virtual_patrol' ? source : undefined
  const schedule = patrol?.schedule
  const series =
    schedule?.mode === 'at' ? detectPatrolInterval(schedule.at) : null

  return {
    patrolVideoSource: patrol?.video_source ?? 'milestone',
    patrolServerId: patrol?.milestone_server?.id ?? '',
    patrolCameras: patrol?.cameras ?? [],
    patrolScheduleMode: series ? 'every' : (schedule?.mode ?? 'at'),
    patrolTimes: toPatrolTimeFormValues(
      schedule?.mode === 'at' ? schedule.at : PATROL_DEFAULT_TIMES
    ),
    patrolTimezone:
      schedule?.mode === 'at' ? schedule.timezone : getBrowserTimezone(),
    patrolInterval:
      series?.interval ??
      (schedule?.mode === 'every' ? schedule.every : PATROL_DEFAULT_INTERVAL),
    patrolStartTime: series?.start ?? PATROL_DEFAULT_START_TIME,
    patrolLegacyEvery: schedule?.mode === 'every',
    patrolClipSeconds: patrol?.clip_seconds ?? PATROL_DEFAULT_CLIP_SECONDS,
    patrolRoundTimeoutMinutes: schedule
      ? roundTimeoutToMinutes(schedule.round_timeout_seconds)
      : PATROL_DEFAULT_ROUND_TIMEOUT_MINUTES,
    // A saved timeout was chosen deliberately; don't replace it with a suggestion.
    patrolRoundTimeoutTouched: !!patrol,
    patrolMilestoneActionsRemoved: false
  }
}

const buildPatrolSchedule = (
  values: PipelineSourceFormValues
): PatrolSchedule => {
  const round_timeout_seconds = roundTimeoutToSeconds(
    values.patrolRoundTimeoutMinutes
  )

  if (values.patrolScheduleMode === 'every' && values.patrolLegacyEvery)
    return {
      mode: 'every',
      every: values.patrolInterval,
      round_timeout_seconds
    }

  const times =
    values.patrolScheduleMode === 'every'
      ? expandPatrolInterval(values.patrolStartTime, values.patrolInterval)
      : getPatrolTimeValues(values.patrolTimes)

  return {
    mode: 'at',
    at: [...new Set(times)].sort(),
    timezone: values.patrolTimezone,
    round_timeout_seconds
  }
}

export const buildPipelineSource = (
  pipelineType: PipelineType,
  values: PipelineSourceFormValues
): PipelineSourceWrite => {
  switch (pipelineType) {
    case 'directory':
      return {
        type: 'directory',
        watch_path: values.watchPath,
        delete_original: values.deleteOriginal
      }
    case 'http_push':
      return {
        type: 'http_push',
        endpoint_path: values.endpointPath,
        port: values.port
      }
    case 'visionplatform':
      return {
        type: 'visionplatform',
        app_ids: [values.applicationId],
        upload_descriptions: values.uploadDescriptions
      }
    case 'milestone':
      return {
        type: 'milestone',
        milestone_server_id: values.milestoneServerId,
        preroll_seconds: values.prerollSeconds,
        postroll_seconds: values.postrollSeconds,
        process_when_states: values.triggerStates,
        cameras: values.milestoneCameras
      }
    case 'virtual_patrol':
      return {
        type: 'virtual_patrol',
        video_source: values.patrolVideoSource,
        ...(values.patrolVideoSource === 'milestone' && {
          milestone_server_id: values.patrolServerId
        }),
        cameras: values.patrolCameras,
        schedule: buildPatrolSchedule(values),
        clip_seconds: values.patrolClipSeconds
      }
  }
}
